import React from "react";
import { VinTextFieldProps } from "./types";
import { isVinValid, getVinError } from "./validation";
import { TextField } from "../TextField";

const VIN_LENGTH = 17;

const VinTextField: React.FC<VinTextFieldProps> = ({
  value,
  onChange,
  error,
  validateOnLength = true,
  placeholder = "Unesite VIN broj (17 znakova)",
  name,
  label,
  ...props
}) => {
  const vin = typeof value === "string" ? value : value ? String(value) : "";

  const isCurrentlyValid = React.useMemo(() => {
    return vin.length === VIN_LENGTH && isVinValid(vin);
  }, [vin]);

  const validationError = React.useMemo(() => {
    if (!vin) return undefined;

    // Check for immediate errors (invalid chars, too long)
    const immediateError = getVinError(vin);
    if (immediateError) return immediateError;

    // If validateOnLength is true, don't show "invalid" until length is 17
    if (validateOnLength && vin.length < VIN_LENGTH) return undefined;

    // Validate complete VIN
    if (!isVinValid(vin)) {
      return "Neispravan VIN broj";
    }

    return undefined;
  }, [vin, validateOnLength]);

  // If VIN is now valid, ignore external error (it will clear on next form validation)
  const displayError = isCurrentlyValid ? undefined : (error || validationError);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Convert to uppercase and remove invalid characters
      const newValue = e.target.value.toUpperCase().slice(0, VIN_LENGTH);

      if (onChange) {
        const event = {
          ...e,
          target: {
            ...e.target,
            name: name || "",
            value: newValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    },
    [onChange, name]
  );

  return (
    <TextField
      {...props}
      type="text"
      name={name}
      label={label}
      value={vin}
      onChange={handleChange}
      error={displayError}
      forceShowError={!!displayError}
      placeholder={placeholder}
      maxLength={VIN_LENGTH}
    />
  );
};

export default React.memo(VinTextField);
